import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { applicationsV2, type ApplicationComparison, type DuplicatePair, type DuplicateSide } from "@/features/applications/api/applications.api";
import { Input } from "@/components/ui/input";
import { StatusPill, APPLICATION_STATUS_TONES, APPLICATION_STATUS_LABELS } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Notice } from "@/components/Notice";
import { Loading } from "@/components/Loading";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toastApiError } from "@/lib/toast";
import { formatDateTime } from "@/utils/dates";

export function DuplicatesPanel({ onCount }: { onCount?: (n: number) => void }) {
  const [pairs, setPairs] = useState<DuplicatePair[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [comparing, setComparing] = useState<DuplicatePair | null>(null);

  const load = useCallback(() => {
    setError(null);
    applicationsV2.duplicates()
      .then((p) => { setPairs(p); onCount?.(p.length); })
      .catch((e) => setError(e instanceof Error ? e.message : "Couldn't load duplicates."));
  }, [onCount]);

  useEffect(() => { load(); }, [load]);

  if (error) return <Notice tone="error">{error}</Notice>;
  if (!pairs) return <Loading>Looking for duplicates…</Loading>;
  if (pairs.length === 0) return <Notice tone="info">No duplicate applications right now.</Notice>;

  return (
    <div className="rounded-md border bg-white shadow-sm">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Business</TableHead>
            <TableHead>Applications</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {pairs.map((p) => (
            <TableRow key={`${p.a.id}-${p.b.id}`}>
              <TableCell className="font-medium text-slate-800">{p.a.name ?? p.b.name ?? "Untitled application"}</TableCell>
              <TableCell className="text-xs text-slate-600">
                <SideLine side={p.a} /> <span className="text-slate-400">and</span> <SideLine side={p.b} />
                <div className="mt-1 text-slate-500">
                  {p.differenceCount === 0 ? "No differences" : `${p.differenceCount} ${p.differenceCount === 1 ? "difference" : "differences"}`}
                  {p.agentAnswer && <> · <AgentAnswer answer={p.agentAnswer} /></>}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <Button type="button" size="sm" onClick={() => setComparing(p)}>
                  {p.differenceCount === 0 ? "Merge" : "Compare"}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {comparing && (
        <CompareDialog
          a={comparing.a.id}
          b={comparing.b.id}
          onClose={() => setComparing(null)}
          onMerged={() => { setComparing(null); load(); }}
        />
      )}
    </div>
  );
}

export function AgentAnswer({ answer }: { answer: "same" | "different" }) {
  return answer === "same"
    ? <span className="font-medium text-emerald-700">Agent says same</span>
    : <span className="font-medium text-amber-700">Agent says different</span>;
}

function SideLine({ side }: { side: DuplicateSide }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <StatusPill value={side.status} map={APPLICATION_STATUS_TONES} labels={APPLICATION_STATUS_LABELS} />
      <span>{side.submittedAt ? formatDateTime(side.submittedAt, { month: "short", day: "numeric" }) : "not submitted"}</span>
    </span>
  );
}

export function CompareDialog({ a, b, onClose, onMerged, openKept = false, onAnswer }: {
  a: string;
  b: string;
  onClose: () => void;
  onMerged: (keptId: string) => void;
  openKept?: boolean;
  onAnswer?: (same: boolean) => void;
}) {
  const navigate = useNavigate();
  const [data, setData] = useState<ApplicationComparison | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    applicationsV2.compare(a, b)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : "Couldn't compare these applications."); });
    return () => { cancelled = true; };
  }, [a, b]);

  async function keep(kept: DuplicateSide, other: DuplicateSide) {
    setSaving(true);
    try {
      await applicationsV2.merge(kept.id, other.id);
      toast.success(`Merged into ${kept.name ?? "the kept application"}. Nothing was deleted.`);
      onMerged(kept.id);
      if (openKept) navigate(`/applications/v2/${kept.id}`);
    } catch (e) {
      toastApiError(e, "Couldn't merge these applications.");
    } finally {
      setSaving(false);
    }
  }

  const submitted = (side: DuplicateSide) => (side.submittedAt ? new Date(side.submittedAt).getTime() : Number.MAX_SAFE_INTEGER);
  const older = data && (submitted(data.a) <= submitted(data.b) ? data.a : data.b);
  const newer = data && older && (older.id === data.a.id ? data.b : data.a);

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Compare applications</DialogTitle>
          <DialogDescription>
            {onAnswer
              ? "Are these the same business? If yes, onboarding combines them into one. Nothing is lost."
              : "Pick the one to keep. The other one's files, notes, underwriting chat and TSYS emails move to it. Nothing is deleted."}
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <Notice tone="error">{error}</Notice>
        ) : !data ? (
          <Loading>Comparing…</Loading>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[data.a, data.b].map((s, i) => (
                <div key={s.id} className="rounded-md border px-3 py-2">
                  <div className="text-xs text-slate-500">{i === 0 ? "A" : "B"}</div>
                  <Link to={`/applications/v2/${s.id}`} className="font-medium text-slate-800 hover:underline">{s.name ?? "Untitled application"}</Link>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                    <StatusPill value={s.status} map={APPLICATION_STATUS_TONES} labels={APPLICATION_STATUS_LABELS} />
                    <span>{s.submittedAt ? formatDateTime(s.submittedAt) : "not submitted"}</span>
                  </div>
                  <div className="text-xs text-slate-500">{s.agentName ?? "No agent"} · {s.documents} {s.documents === 1 ? "file" : "files"}</div>
                </div>
              ))}
            </div>
            {!onAnswer && data.a.agentName !== data.b.agentName && (
              <Notice tone="warn" className="px-3 py-2">
                Different agents: {data.a.agentName ?? "no agent"} and {data.b.agentName ?? "no agent"}. Only the kept application's agent stays on it.
              </Notice>
            )}
            {data.differences.length === 0 ? (
              <Notice tone="success" className="px-3 py-2">No differences. Merging keeps the older one ({older?.name}).</Notice>
            ) : (
              <div className="max-h-72 overflow-y-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Different</TableHead>
                      <TableHead>A</TableHead>
                      <TableHead>B</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.differences.map((d) => (
                      <TableRow key={d.label}>
                        <TableCell className="text-xs text-slate-500">{d.label}</TableCell>
                        <TableCell className="text-sm">{d.a ?? <span className="text-slate-400">empty</span>}</TableCell>
                        <TableCell className="text-sm">{d.b ?? <span className="text-slate-400">empty</span>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>{onAnswer ? "Close" : "Cancel"}</Button>
          {onAnswer && data && (
            <>
              <Button type="button" variant="outline" onClick={() => onAnswer(false)}>No, different</Button>
              <Button type="button" onClick={() => onAnswer(true)}>Yes, same business</Button>
            </>
          )}
          {!onAnswer && data && data.differences.length === 0 && older && newer && (
            <Button type="button" onClick={() => keep(older, newer)} disabled={saving}>{saving ? "Merging…" : "Merge"}</Button>
          )}
          {!onAnswer && data && data.differences.length > 0 && (
            <>
              <Button type="button" onClick={() => keep(data.a, data.b)} disabled={saving}>Keep A</Button>
              <Button type="button" onClick={() => keep(data.b, data.a)} disabled={saving}>Keep B</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export interface MergeCandidate {
  id: string;
  name: string | null;
  status: string;
  agentName: string | null;
}

const sameName = (a: string | null, b: string | null) =>
  !!a && !!b && a.trim().toLowerCase().replace(/[^a-z0-9]/g, "") === b.trim().toLowerCase().replace(/[^a-z0-9]/g, "");

async function searchCandidates(keyword: string): Promise<MergeCandidate[]> {
  const r = await applicationsV2.list({ pageSize: 20, keyword });
  return r.items
    .filter((a) => a.status !== "Merged")
    .map((a) => ({ id: a.id, name: a.dbaName ?? a.legalName, status: a.status, agentName: a.owner?.name ?? null }));
}

export async function findMergeCandidates(appId: string, name: string | null): Promise<{ self: MergeCandidate | null; others: MergeCandidate[] }> {
  const [pairs, found, detail] = await Promise.all([
    applicationsV2.duplicates(appId).catch(() => [] as DuplicatePair[]),
    name ? searchCandidates(name).catch(() => [] as MergeCandidate[]) : Promise.resolve([] as MergeCandidate[]),
    applicationsV2.get(appId).catch(() => null),
  ]);
  const listed = found.find((c) => c.id === appId);
  const self: MergeCandidate | null = detail
    ? { id: detail.id, name: listed?.name ?? name, status: detail.status, agentName: detail.owner?.name ?? listed?.agentName ?? null }
    : listed ?? null;
  const others = new Map<string, MergeCandidate>();
  for (const p of pairs) {
    const other = p.a.id === appId ? p.b : p.a;
    others.set(other.id, { id: other.id, name: other.name, status: other.status, agentName: other.agentName });
  }
  for (const c of found) if (c.id !== appId && sameName(c.name, name)) others.set(c.id, c);
  return { self, others: [...others.values()] };
}

export function MergeWithDialog({ app, agentName, initial, linked = false, linkedWhat = "TSYS update", onClose, onMerged }: {
  app: { id: string; name: string | null };
  agentName: string | null;
  initial?: MergeCandidate[];
  linked?: boolean;
  linkedWhat?: string;
  onClose: () => void;
  onMerged: () => void;
}) {
  const [keyword, setKeyword] = useState(linked ? "" : app.name ?? "");
  const [options, setOptions] = useState<MergeCandidate[] | null>(initial ?? null);
  const [picked, setPicked] = useState<string | null>(initial?.length === 1 ? initial[0].id : null);
  const [saving, setSaving] = useState(false);
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    if (linked) return;
    let cancelled = false;
    const t = setTimeout(() => {
      searchCandidates(keyword)
        .then((c) => { if (!cancelled) setOptions(c.filter((o) => o.id !== app.id)); })
        .catch(() => { if (!cancelled) setOptions([]); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [keyword, linked, app.id]);

  const choice = options?.find((o) => o.id === picked) ?? null;
  const label = app.name ?? "this application";

  async function merge() {
    if (!choice) return;
    setSaving(true);
    try {
      await applicationsV2.merge(app.id, choice.id);
      toast.success(`Merged into ${label}. Nothing was deleted.`);
      onMerged();
    } catch (e) {
      toastApiError(e, "Couldn't merge these applications.");
    } finally {
      setSaving(false);
    }
  }

  if (comparing && choice)
    return <CompareDialog a={app.id} b={choice.id} onClose={() => setComparing(false)} onMerged={() => onMerged()} />;

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{linked ? `Merge into ${label}?` : "Merge with another application"}</DialogTitle>
          <DialogDescription>
            {linked
              ? `You linked the ${linkedWhat} to ${label}. ${options && options.length > 1 ? "These look" : "This looks"} like the same business.`
              : `Pick the extra application. It moves into ${label} and is hidden. Nothing is deleted, and an admin can un-merge it.`}
          </DialogDescription>
        </DialogHeader>
        {!linked && (
          <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="Search business name…" aria-label="Search applications" />
        )}
        {!options ? (
          <Loading>Looking…</Loading>
        ) : options.length === 0 ? (
          <Notice tone="info">No other applications match.</Notice>
        ) : (
          <div className="max-h-72 divide-y overflow-y-auto rounded-md border">
            {options.map((o) => (
              <label key={o.id} className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50">
                <input type="radio" name="merge-with" checked={picked === o.id} onChange={() => setPicked(o.id)} />
                <span className="flex-1">
                  <span className="font-medium text-slate-800">{o.name ?? "Untitled application"}</span>
                  <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                    <StatusPill value={o.status} map={APPLICATION_STATUS_TONES} labels={APPLICATION_STATUS_LABELS} />
                    {o.agentName ?? "No agent"}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}
        {choice && choice.agentName !== agentName && (
          <Notice tone="warn" className="px-3 py-2">
            Different agents: this one is {agentName ?? "no agent"}'s, the other is {choice.agentName ?? "no agent"}'s. Only {agentName ?? "no agent"} stays on it.
          </Notice>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>{linked ? "Keep both" : "Cancel"}</Button>
          <Button type="button" variant="outline" onClick={() => setComparing(true)} disabled={!choice || saving}>Compare first</Button>
          <Button type="button" onClick={merge} disabled={!choice || saving}>{saving ? "Merging…" : `Merge into ${label}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
